import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useTheme } from 'styled-components'
import { PageButton, PageButtons, PaginationBar, PaginationInfo } from './BusinessTable.styled'

interface Props {
  page: number
  pageCount: number
  shownCount: number
  totalCount: number
  onPage: (page: number) => void
  summary?: string
}

const pageNumbers = (page: number, pageCount: number): number[] => {
  const pages = new Set<number>([1, pageCount, page - 1, page, page + 1])
  return [...pages]
    .filter(candidate => candidate >= 1 && candidate <= pageCount)
    .sort((a, b) => a - b)
}

export const TablePagination: React.FC<Props> = ({
  page,
  pageCount,
  shownCount,
  totalCount,
  onPage,
  summary
}) => {
  const [t] = useTranslation()
  const theme = useTheme()

  return (
    <PaginationBar>
      <PaginationInfo aria-live="polite">
        {summary ?? t('businessesPage.table.showing', { shown: shownCount, total: totalCount })}
      </PaginationInfo>
      <PageButtons>
        <PageButton
          type="button"
          $chevron
          $disabled={page <= 1}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <FaChevronLeft style={{ fontSize: `${theme.fontSize.xs}rem` }} />
        </PageButton>
        {pageNumbers(page, pageCount).map((pageNumber, index, pages) => (
          <React.Fragment key={pageNumber}>
            {index > 0 && pages[index - 1] < pageNumber - 1 && (
              <span style={{ fontSize: `${theme.fontSize.xs}rem`, fontWeight: 700 }}>…</span>
            )}
            <PageButton
              type="button"
              $current={pageNumber === page}
              onClick={() => pageNumber !== page && onPage(pageNumber)}
            >
              {pageNumber}
            </PageButton>
          </React.Fragment>
        ))}
        <PageButton
          type="button"
          $chevron
          $disabled={page >= pageCount}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          <FaChevronRight style={{ fontSize: `${theme.fontSize.xs}rem` }} />
        </PageButton>
      </PageButtons>
    </PaginationBar>
  )
}
